import React from "react"
import autobind from "class-autobind"
import { Dialog, FlatButton, TextField, SelectField, MenuItem, Divider } from "material-ui"
import strings from "../../../../localization/strings"
import { EnumValues } from "enum-values"
import { ICustomReportDocument, ECustomReportType, ECustomReportLocation, EDateSelectionType } from "../../../../models"
import * as utils from "../../../../helpers/utils"
import ScriptCodeEditor from "../../../../components/common/script_code_editor/ScriptCodeEditor"
import AccountStore from "../../../../stores/AccountStore"
import * as api from "../../../../api"
import { red500, green500 } from "material-ui/styles/colors"
import AppStateStore from "../../../../stores/AppStateStore"

interface IProps {
    existingCustomReports: ICustomReportDocument[]
    editCustomReport: ICustomReportDocument
    handleClose(): void
    handleAdd(customReport: ICustomReportDocument): void
    handleUpdate(customReport: ICustomReportDocument): void
}
interface IState {
    name: string
    errorName: string
    type: ECustomReportType
    errorType: string
    reportLocation: ECustomReportLocation
    errorReportLocation: string
    reportDateSelectionType: EDateSelectionType
    scriptCode: string
    errorScriptCode: string
    defaultFileName: string
    errorDefaultFileName: string
    fileExtension: string
    compileErrors: boolean
    compileErrorsText: string
}

export default class AddCustomReportDialog extends React.Component<IProps, IState> {
    private availableTypes: ECustomReportType[]
    private availableReportLocations: ECustomReportLocation[]
    constructor(props: IProps) {
        super(props)
        this.state = {
            name: props.editCustomReport ? props.editCustomReport.Name : "",
            errorName: "",
            type: props.editCustomReport ? props.editCustomReport.Type : null,
            errorType: "",
            reportLocation: props.editCustomReport ? props.editCustomReport.Location : ECustomReportLocation.None,
            errorReportLocation: "",
            reportDateSelectionType: props.editCustomReport ? props.editCustomReport.DateSelectionType : EDateSelectionType.NoSelection,
            scriptCode: props.editCustomReport ? props.editCustomReport.CSharpScript : "",
            errorScriptCode: "",
            defaultFileName: props.editCustomReport ? props.editCustomReport.DefaultFileName : "download.csv",
            errorDefaultFileName: "",
            fileExtension: props.editCustomReport ? props.editCustomReport.FileExtension : ".csv",
            compileErrors: false,
            compileErrorsText: "",
        }
        autobind(this)

        let excludeTypes = props.existingCustomReports.map(i => i.Type)
        if (props.editCustomReport)
            excludeTypes = excludeTypes.filter(i => i !== props.editCustomReport.Type)
        //NewCutomReportType should be show always. and show as new section.        
        excludeTypes.push(ECustomReportType.NewCustomReportType)

        this.availableTypes = (EnumValues.getValues(ECustomReportType) as ECustomReportType[]).filter(i => !excludeTypes.includes(i))

        this.availableReportLocations = (EnumValues.getValues(ECustomReportLocation) as ECustomReportLocation[]).filter(i => i !== ECustomReportLocation.None)
    }

    handleSave() {
        if (!this.checkValidate()) return
        AppStateStore.setProgressSnackbarContent("loading")
        const payload: ICustomReportDocument = this.getPayload()
        api.compileCheckCustomReportDoc(payload).then(res =>
            res.data.length !== 0 ?
                this.setState({
                    compileErrors: true,
                    compileErrorsText: res.data,
                }, () => AppStateStore.setProgressSnackbarContent(null)) :
                this.props.editCustomReport ?
                    this.props.handleUpdate(payload) :
                    this.props.handleAdd(payload)

        )
    }

    getPayload() {
        const payload: ICustomReportDocument = {
            Id: this.props.editCustomReport ? this.props.editCustomReport.Id : utils.guid(),
            AccountId: AccountStore.getCurrentAccount().Id,
            Name: this.state.name.trim(),
            Type: this.state.type,
            Location: this.state.type !== ECustomReportType.NewCustomReportType ? ECustomReportLocation.None : this.state.reportLocation,
            DateSelectionType: this.state.type !== ECustomReportType.NewCustomReportType ? EDateSelectionType.NoSelection : this.state.reportDateSelectionType,
            CSharpScript: this.state.scriptCode.trim(),
            DefaultFileName: this.state.defaultFileName.trim(),
            FileExtension: this.state.fileExtension.trim(),
            Version: this.props.editCustomReport ? this.props.editCustomReport.Version : null
        }
        return payload
    }

    checkValidate() {
        let isValidate = true
        if (!this.state.name.trim()) {
            this.setState({ errorName: strings.NameRequired })
            isValidate = false
        }
        else {
            this.setState({ errorName: "" })
        }

        if (this.state.type === null) {
            this.setState({ errorType: strings.This_field_is_required })
            isValidate = false
        }
        else {
            this.setState({ errorType: "" })
        }

        if (!this.state.defaultFileName.trim()) {
            this.setState({ errorDefaultFileName: strings.This_field_is_required })
            isValidate = false
        }
        else {
            this.setState({ errorDefaultFileName: "" })
        }

        if (this.state.type === ECustomReportType.NewCustomReportType) {
            if (this.state.reportLocation === ECustomReportLocation.None) {
                this.setState({ errorReportLocation: strings.Please_select_location })
                isValidate = false
            }
            else {
                this.setState({ errorReportLocation: "" })
            }
        }
        else {
            this.setState({ errorReportLocation: "" })
        }

        if (!this.state.scriptCode.trim()) {
            this.setState({ errorScriptCode: strings.This_field_is_required })
            isValidate = false
        }
        else {
            this.setState({ errorScriptCode: "" })
        }

        return isValidate
    }

    render() {
        return (
            <Dialog
                open
                title={this.props.editCustomReport ? strings.Edit_Custom_Report : strings.Add_Custom_Report}
                titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
                contentStyle={{ width: "95%", maxWidth: "none" }}
                autoScrollBodyContent
                actions={[
                    <FlatButton
                        data-intercom-target={"menu-AccountSettings-CustomReports-Btn-CloseAddCustomReportDialog"}
                        label={strings.CANCEL}
                        secondary
                        onClick={this.props.handleClose}
                    />,
                    <FlatButton
                        data-intercom-target={"menu-AccountSettings-CustomReports-Btn-SaveCustomReport"}
                        label={strings.SAVE}
                        primary
                        onClick={this.handleSave}
                    />
                ]}
            >
                <div style={{ paddingRight: 20, paddingLeft: 20 }}>
                    <div style={{ display: "flex" }}>
                        <TextField
                            data-intercom-target={"menu-AccountSettings-CustomReports-Add-Entry-Name"}
                            fullWidth
                            style={{ marginRight: 50 }}
                            floatingLabelText={strings.Name}
                            value={this.state.name}
                            onChange={(e, value) => { this.setState({ name: value }, () => this.checkValidate()) }}
                            errorText={this.state.errorName}
                            errorStyle={{ position: "absolute", bottom: -10 }}
                        />
                    </div>
                    <div style={{ display: "flex", marginTop: 15 }}>
                        <SelectField
                            data-intercom-target={"menu-AccountSettings-CustomReports-Add-Select-CustomReportType"}
                            disabled={this.props.editCustomReport ? true : false}
                            style={{ width: 300 }}
                            floatingLabelText={strings.Custom_Report_Type}
                            value={this.state.type}
                            onChange={(event, index, value) => this.setState({ type: value }, () => this.checkValidate())}
                            errorText={this.state.errorType}
                            errorStyle={{ position: "absolute", bottom: -10 }}
                        >
                            {this.availableTypes.map(item =>
                                <MenuItem key={item} value={item} primaryText={strings[EnumValues.getNameFromValue(ECustomReportType, item)]} />
                            )}
                            <Divider />
                            <MenuItem value={ECustomReportType.NewCustomReportType} primaryText={strings.NewCustomReportType} />
                        </SelectField>
                        {this.state.type === ECustomReportType.NewCustomReportType && [
                            <SelectField
                                key="ReportLocation"
                                autoWidth
                                data-intercom-target={"menu-AccountSettings-CustomReports-ReportLocation"}
                                style={{ width: 250, marginLeft: 25 }}
                                floatingLabelText={strings.Report_Location}
                                value={this.state.reportLocation}
                                onChange={(event, index, value) => this.setState({ reportLocation: value }, () => this.checkValidate())}
                                errorText={this.state.errorReportLocation}
                            >
                                {this.availableReportLocations.map(item =>
                                    <MenuItem
                                        key={item}
                                        value={item}
                                        primaryText={strings[EnumValues.getNameFromValue(ECustomReportLocation, item)]}
                                    />
                                )}
                            </SelectField>,
                            <SelectField
                                key="DateSelectionType"
                                data-intercom-target={"menu-AccountSettings-CustomReports-DateSelectionType"}
                                style={{ marginLeft: 25, width: 200 }}
                                floatingLabelText={strings.Date_Selection_Type}
                                value={this.state.reportDateSelectionType}
                                onChange={(event, index, value) => this.setState({ reportDateSelectionType: value })}
                            >
                                {EnumValues.getValues(EDateSelectionType).map(item =>
                                    <MenuItem
                                        key={item}
                                        value={item}
                                        primaryText={strings[EnumValues.getNameFromValue(EDateSelectionType, item)]}
                                    />
                                )}
                            </SelectField>
                        ]}
                        {this.state.type !== ECustomReportType.TimecardValidationScript &&
                            <TextField
                                data-intercom-target={"menu-AccountSettings-CustomReports-Add-Entry-DefaultFileName"}
                                style={{ marginLeft: 25 }}
                                floatingLabelText={strings.Default_File_Name}
                                value={this.state.defaultFileName}
                                onChange={(e, value) => this.setState({ defaultFileName: value }, () => this.checkValidate())}
                                errorText={this.state.errorDefaultFileName}
                                errorStyle={{ position: "absolute", bottom: -10 }}
                            />
                        }

                        {this.state.type !== ECustomReportType.TimecardValidationScript &&
                            <SelectField
                                data-intercom-target={"menu-AccountSettings-CustomReports-Add-Select-FileExtension"}
                                style={{ width: 100, marginLeft: 25 }}
                                floatingLabelText={strings.File_Extension}
                                value={this.state.fileExtension}
                                onChange={(event, index, value) => this.setState({ fileExtension: value })}
                            >
                                <MenuItem value=".csv" primaryText={".csv"} />
                                <MenuItem value=".pdf" primaryText={".pdf"} />
                                <MenuItem value=".zip" primaryText={".zip"} />
                            </SelectField>
                        }
                    </div>
                    {this.state.compileErrors &&
                        <div style={{ color: red500 }}>
                            <pre>{this.state.compileErrorsText}</pre>
                        </div>
                    }
                    {!this.state.compileErrors &&
                        <div style={{ color: green500, marginTop: 15 }}>
                            <i className="fa fa-check" style={{ fontSize: 15, color: green500, fontWeight: "bold" }} /> {strings.NoCompileError}
                        </div>
                    }
                    <ScriptCodeEditor
                        scriptCode={this.state.scriptCode}
                        errorScriptCode={this.state.errorScriptCode}
                        handleChange={scriptCode => this.setState({ scriptCode }, () => this.checkValidate())}
                    />
                </div>
            </Dialog >
        )
    }
}